import React from 'react'
import { Check, ChevronDown, Search, X } from 'lucide-react'

export interface EntityOption {
  id: number
  name: string
}

export interface EntityMultiSelectProps {
  label: string
  options: EntityOption[]
  selected: number[]
  onChange: (next: number[]) => void
  placeholder?: string
  className?: string
}

/**
 * Multi-select dropdown for entities with a stable numeric ID + display name.
 * Mirrors `MultiSelect` but operates on `{id, name}` pairs so two entities can
 * share the same display string without collision (e.g. two developers named
 * the same brand).
 */
export function EntityMultiSelect({
  label,
  options,
  selected,
  onChange,
  placeholder,
  className,
}: EntityMultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const rootRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (!rootRef.current) return
      if (!rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', onDocMouseDown)
      document.addEventListener('keydown', onKey)
      return () => {
        document.removeEventListener('mousedown', onDocMouseDown)
        document.removeEventListener('keydown', onKey)
      }
    }
  }, [open])

  const filtered = React.useMemo(() => {
    if (!query.trim()) return options
    const q = query.toLowerCase()
    return options.filter((o) => o.name.toLowerCase().includes(q))
  }, [options, query])

  const selectedById = React.useMemo(() => {
    const map = new Map<number, string>()
    for (const o of options) map.set(o.id, o.name)
    return map
  }, [options])

  function toggle(id: number) {
    if (selected.includes(id)) {
      onChange(selected.filter((v) => v !== id))
    } else {
      onChange([...selected, id])
    }
  }

  function clearAll(e: React.MouseEvent) {
    e.stopPropagation()
    onChange([])
  }

  const count = selected.length
  const triggerLabel =
    count === 0
      ? placeholder ?? `All ${label.toLowerCase()}`
      : count === 1
        ? selectedById.get(selected[0]) ?? `#${selected[0]}`
        : `${count} selected`

  return (
    <div className={`relative ${className ?? ''}`} ref={rootRef}>
      <label className="block text-xs font-medium text-secondary-500 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between gap-2 min-w-[180px] border border-secondary-200 rounded-lg px-3 py-1.5 text-sm bg-white hover:border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <span className={count === 0 ? 'text-secondary-400' : 'text-secondary-800'}>
          {triggerLabel}
        </span>
        <div className="flex items-center gap-1">
          {count > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={clearAll}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') clearAll(e as unknown as React.MouseEvent)
              }}
              className="rounded-full p-0.5 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
              aria-label={`Clear ${label}`}
              title="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-secondary-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-72 rounded-lg border border-secondary-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-secondary-100 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-secondary-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="w-full bg-transparent text-sm focus:outline-none"
            />
          </div>
          <ul className="max-h-60 overflow-auto py-1 text-sm">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-secondary-400">No matches</li>
            )}
            {filtered.map((opt) => {
              const checked = selected.includes(opt.id)
              return (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => toggle(opt.id)}
                    className={`flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-secondary-50 ${checked ? 'bg-primary-50 text-primary-700' : 'text-secondary-700'}`}
                  >
                    <span className="truncate">{opt.name}</span>
                    {checked && <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
